import { deviceSizes, breakpoints as breakPointSizes } from '../style';
import { mobileFirstBreakPoints } from '../style/device';

export type CssUnitsAsResponsiveObject<T> = T | { [key in keyof typeof mobileFirstBreakPoints]?: T };

// this function take a css property ('flex-direction') and a value ('row') and returns a css string "flex-direction: row;".
// in case the user passed an object for the value, the function will return a string with media queries for every breakpoint.
// the object is supposed to look like this: { sm: 'column', md: 'row' }
export const getCssRuleWithResponsiveCalculation = <Type = string | number>({
  property,
  value,
  formatValue = value => value,
}: {
  // formatValue is for when we want to change the value before returning it. for example, if we want to add 'px' to a number.
  formatValue?: (value?: Type) => string | number | Type | undefined;
  property: string;
  value?: string | number | { [key in keyof typeof mobileFirstBreakPoints]?: Type };
}): string => {
  if (typeof value === 'object') {
    // this array will hold the css string for every breakpoint
    const mediaQueriesCss = [] as string[];

    // default value is not related to breakpoints and is used as default value that is not on the breakpoints list
    // copying the object to a new object so we can delete the default prop from it
    const breakPointsObjectWithCssValues = { ...value };

    const breakPointKeys = Object.keys(breakPointsObjectWithCssValues) as Array<keyof typeof deviceSizes>;
    // sorting the order of breakpoints by size because css takes always the latest value of breakpoints by declaration order.
    const sortedBreakPointKeys = breakPointKeys.sort((a, b) => breakPointSizes[a] - breakPointSizes[b]);

    for (const breakPoint of sortedBreakPointKeys) {
      mediaQueriesCss.push(`
			@media (min-width: ${mobileFirstBreakPoints[breakPoint]}px) {
				${property}: ${formatValue(value[breakPoint])};
			};
		`);
    }

    return mediaQueriesCss.join('');
  }

  // else, if value is a string
  if (property && value) {
    return `${property}: ${formatValue(value as Type)};`;
  }

  // else, no property or value, return empty string and do nothing
  return '';
};
